<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { refuge } from '~/data/refuge'

const videoEl = ref<HTMLVideoElement | null>(null)
const isOnline = ref(false)
let player: any = null

onMounted(async () => {
  const videojs = (await import('video.js')).default

  player = videojs(videoEl.value!, {
    autoplay: 'muted',
    muted: true,
    liveui: true,
    responsive: true,
    fluid: true,
    controls: true,
    html5: { nativeControlsForTouch: false },
    sources: [{
      src: 'https://mediacp.alphastream.eu:443/kataraktis/index.m3u8',
      type: 'application/x-mpegURL',
    }],
    controlBar: {
      children: [
        'playToggle',
        'volumePanel',
        'liveDisplay',
        'pictureInPictureToggle',
        'fullscreenToggle',
      ],
    },
  })

  player.on('playing', () => { isOnline.value = true })
  player.on(['waiting', 'error'], () => { isOnline.value = false })
})

onBeforeUnmount(() => {
  player?.dispose()
})
</script>

<template>
  <div class="meteo-camera">
    <div class="player-wrapper">
      <video
          ref="videoEl"
          class="video-js vjs-default-skin vjs-big-play-centered"
          playsinline
      />
    </div>

    <div class="camera-footer">
      <div class="footer-left">
        <Icon name="ph:map-pin-fill" class="pin-icon" size="0.75rem" />
        <span class="camera-name">{{ $t('RefugeName') }}</span>
        <div class="status-badge" :class="isOnline ? 'is-online' : 'is-offline'">
          <span class="dot" />
          <span class="status-label">{{ isOnline ? 'LIVE' : 'OFFLINE' }}</span>
        </div>
      </div>

      <div class="footer-right">
        <span class="elevation">{{ refuge.elevationMetres }}m</span>
        <a
        href="https://meteolive.gr"
        target="_blank"
        rel="noopener noreferrer"
        class="credit"
        >
        powered by <strong>meteolive.gr</strong>
        <span class="sr-only">({{ $t('opensInNewTab') }})</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.meteo-camera
  width: 100%
  border-radius: 16px
  overflow: hidden
  background: #0a0a0a
  border: 1px solid rgba(255, 255, 255, 0.07)

.player-wrapper
  width: 100%
  line-height: 0

  :deep(.video-js)
    width: 100% !important
    height: auto !important
    aspect-ratio: 16/9

.camera-footer
  display: flex
  justify-content: space-between
  align-items: center
  padding: 0.6rem 1rem
  background: #080808
  border-top: 1px solid rgba(255, 255, 255, 0.06)
  flex-wrap: wrap
  gap: 0.5rem

.footer-left
  display: flex
  align-items: center
  gap: 0.4rem

.pin-icon
  color: $color-primary
  flex-shrink: 0
  animation: pulse 1.5s infinite

.camera-name
  font-size: 0.75rem
  font-weight: 700
  color: #bbb
  white-space: nowrap

.status-badge
  display: inline-flex
  align-items: center
  gap: 0.3rem
  margin-left: 0.25rem

.dot
  width: 5px
  height: 5px
  border-radius: 50%
  flex-shrink: 0

.status-label
  font-size: 0.65rem
  font-weight: 700
  letter-spacing: 0.5px

.is-online
  .dot
    background: #00ff88
  .status-label
    color: #00ff88

.is-offline
  .dot
    background: $color-primary
    animation: pulse 0.8s infinite
  .status-label
    color: $color-primary

.footer-right
  display: flex
  align-items: center
  gap: 0.75rem

.elevation
  font-family: 'JetBrains Mono', monospace
  font-size: 0.7rem
  font-weight: 700
  color: #555

.credit
  font-size: 0.7rem
  color: #555
  text-decoration: none
  font-weight: 500
  transition: color 0.2s

  &:hover
    color: #fff

  strong
    color: #00a8ff

@keyframes pulse
  0%, 100%
    opacity: 1
  50%
    opacity: 0.3
</style>